import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlowCard } from '../components/GlowCard.js';

interface Pipeline {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'draft' | 'error';
  sourcePlatform: string;
  outputPlatforms: string[];
  voiceProfile: { description?: string } | null;
  isActive: boolean;
  contentCount: number;
  createdAt: string;
  updatedAt: string;
}

const TARGET_PLATFORMS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
];

const STATUS_STYLES: Record<Pipeline['status'], string> = {
  active: 'bg-green-500/10 text-green-400 ring-1 ring-green-500/20',
  paused: 'bg-yellow-500/10 text-yellow-400 ring-1 ring-yellow-500/20',
  draft: 'bg-white/5 text-white/60',
  error: 'bg-red-500/10 text-red-400 ring-1 ring-red-500/20',
};

function PlatformBadge({ platform }: { platform: string }) {
  const labels: Record<string, string> = {
    youtube: 'YT',
    instagram: 'IG',
    facebook: 'FB',
    linkedin: 'LI',
    tiktok: 'TK',
  };
  const colors: Record<string, string> = {
    youtube: 'bg-red-500/20 text-red-400',
    instagram: 'bg-pink-500/20 text-pink-400',
    facebook: 'bg-blue-500/20 text-blue-400',
    linkedin: 'bg-sky-500/20 text-sky-400',
    tiktok: 'bg-white/10 text-white',
  };
  return (
    <span
      className={[
        'inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold',
        colors[platform] ?? 'bg-white/5 text-white/60',
      ].join(' ')}
    >
      {labels[platform] ?? platform.slice(0, 2).toUpperCase()}
    </span>
  );
}

interface PipelineFormState {
  name: string;
  outputPlatforms: string[];
  voiceProfileDescription: string;
  isActive: boolean;
}

const DEFAULT_FORM: PipelineFormState = {
  name: '',
  outputPlatforms: [],
  voiceProfileDescription: '',
  isActive: false,
};

interface ModalProps {
  title: string;
  initial: PipelineFormState;
  saving: boolean;
  onSave: (data: PipelineFormState) => void;
  onClose: () => void;
}

function PipelineModal({ title, initial, saving, onSave, onClose }: ModalProps) {
  const [form, setForm] = useState<PipelineFormState>(initial);

  function togglePlatform(p: string) {
    setForm((prev) => ({
      ...prev,
      outputPlatforms: prev.outputPlatforms.includes(p)
        ? prev.outputPlatforms.filter((x) => x !== p)
        : [...prev.outputPlatforms, p],
    }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave(form);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-xl bg-pix-deep shadow-xl">
        <div className="border-b border-white/5 px-6 py-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          <div>
            <label className="block text-sm font-medium text-white/70" htmlFor="pipeline-name">
              Name
            </label>
            <input
              id="pipeline-name"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="My TikTok pipeline"
              className="mt-1 block w-full rounded-lg border border-white/15 bg-pix-void px-3 py-2 text-sm text-white placeholder-white/30 focus:border-pix-pink/50 focus:outline-none focus:ring-1 focus:ring-pix-pink/50"
            />
          </div>

          <div>
            <p className="text-sm font-medium text-white/70">Source</p>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-white/10 bg-pix-void px-3 py-2">
              <PlatformBadge platform="tiktok" />
              <span className="text-sm text-white/50">TikTok (source only in M3)</span>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-white/70">Target platforms</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {TARGET_PLATFORMS.map((p) => {
                const checked = form.outputPlatforms.includes(p.value);
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => togglePlatform(p.value)}
                    className={[
                      'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                      checked
                        ? 'border-pix-blue/50 bg-pix-blue/10 text-pix-blue'
                        : 'border-white/10 text-white/60 hover:border-white/15 hover:bg-pix-void',
                    ].join(' ')}
                  >
                    <PlatformBadge platform={p.value} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium text-white/70"
              htmlFor="voice-profile"
            >
              Voice profile
            </label>
            <textarea
              id="voice-profile"
              rows={3}
              value={form.voiceProfileDescription}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, voiceProfileDescription: e.target.value }))
              }
              placeholder="Describe your brand voice (e.g. professional but approachable, avoids jargon, always ends with a question)"
              className="mt-1 block w-full rounded-lg border border-white/15 bg-pix-void px-3 py-2 text-sm text-white placeholder-white/30 focus:border-pix-pink/50 focus:outline-none focus:ring-1 focus:ring-pix-pink/50"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-white/70">Active</span>
              <p className="text-xs text-white/40 mt-0.5">
                When on, this pipeline polls your TikTok every 15 min and automatically
                reposts to your selected platforms. Turn off to pause all activity.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.isActive}
              onClick={() => setForm((prev) => ({ ...prev, isActive: !prev.isActive }))}
              className={[
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                form.isActive ? 'bg-pix-pink' : 'bg-gray-200',
              ].join(' ')}
            >
              <span
                className={[
                  'inline-block h-4 w-4 transform rounded-full bg-pix-deep shadow transition-transform',
                  form.isActive ? 'translate-x-6' : 'translate-x-1',
                ].join(' ')}
              />
            </button>
          </div>

          <div className="flex justify-end gap-3 border-t border-white/5 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-white/60 hover:bg-pix-void"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name || form.outputPlatforms.length === 0}
              className="rounded-lg bg-pix-pink px-4 py-2 text-sm font-medium text-white hover:bg-pix-pink-dark disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save pipeline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PipelinesPage() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [triggeringId, setTriggeringId] = useState<string | null>(null);

  useEffect(() => {
    loadPipelines();
  }, []);

  async function loadPipelines() {
    setLoading(true);
    setError(null);
    const result = await api.get<Pipeline[]>('/api/pipelines');
    if (result.data) {
      setPipelines(result.data);
    } else {
      setError('Failed to load pipelines.');
    }
    setLoading(false);
  }

  async function handleCreate(form: PipelineFormState) {
    setSaving(true);
    const result = await api.post<Pipeline>('/api/pipelines', {
      name: form.name,
      sourcePlatform: 'tiktok',
      outputPlatforms: form.outputPlatforms,
      voiceProfile: form.voiceProfileDescription
        ? { description: form.voiceProfileDescription }
        : null,
      isActive: form.isActive,
    });
    setSaving(false);
    if (result.data) {
      setPipelines((prev) => [{ ...result.data!, contentCount: 0 }, ...prev]);
      setShowCreate(false);
    }
  }

  async function handleEdit(form: PipelineFormState) {
    if (!editingId) return;
    setSaving(true);
    const result = await api.patch<Pipeline>(`/api/pipelines/${editingId}`, {
      name: form.name,
      outputPlatforms: form.outputPlatforms,
      voiceProfile: form.voiceProfileDescription
        ? { description: form.voiceProfileDescription }
        : null,
      isActive: form.isActive,
    });
    setSaving(false);
    if (result.data) {
      setPipelines((prev) =>
        prev.map((p) =>
          p.id === editingId ? { ...result.data!, contentCount: p.contentCount } : p
        )
      );
      setEditingId(null);
    }
  }

  async function handleToggleActive(pipeline: Pipeline) {
    const result = await api.patch<Pipeline>(`/api/pipelines/${pipeline.id}`, {
      isActive: !pipeline.isActive,
    });
    if (result.data) {
      setPipelines((prev) =>
        prev.map((p) =>
          p.id === pipeline.id
            ? { ...result.data!, contentCount: p.contentCount }
            : p
        )
      );
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    await api.delete(`/api/pipelines/${id}`);
    setPipelines((prev) => prev.filter((p) => p.id !== id));
    setDeletingId(null);
  }

  async function handleRun(id: string) {
    setTriggeringId(id);
    await api.post(`/api/pipelines/${id}/run`, {});
    setTriggeringId(null);
  }

  const editingPipeline = pipelines.find((p) => p.id === editingId);
  const editInitial: PipelineFormState | null = editingPipeline
    ? {
        name: editingPipeline.name,
        outputPlatforms: editingPipeline.outputPlatforms,
        voiceProfileDescription: editingPipeline.voiceProfile?.description ?? '',
        isActive: editingPipeline.isActive,
      }
    : null;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Pipelines</h1>
          <p className="mt-1 text-sm text-white/50">
            Automate content distribution from TikTok to your target platforms.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-lg bg-pix-pink px-4 py-2 text-sm font-medium text-white hover:bg-pix-pink-dark"
        >
          New pipeline
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400 ring-1 ring-red-500/20">
          {error}
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-pix-pink border-t-transparent" />
          </div>
        ) : pipelines.length === 0 ? (
          <EmptyState
            icon={
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
              </svg>
            }
            headline="No pipelines yet"
            subtext="Create your first pipeline to start distributing TikTok content across platforms."
            cta={{ label: 'Create your first pipeline', onClick: () => setShowCreate(true) }}
          />
        ) : (
          pipelines.map((pipeline) => (
            <GlowCard
              key={pipeline.id}
              className="overflow-hidden rounded-xl border border-white/10 bg-pix-deep"
            >
              <div className="flex items-start justify-between px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-white">
                      {pipeline.name}
                    </p>
                    <span
                      className={[
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_STYLES[pipeline.status],
                      ].join(' ')}
                    >
                      {pipeline.status}
                    </span>
                  </div>

                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <PlatformBadge platform="tiktok" />
                      <svg
                        className="h-3 w-3 text-white/40"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      <div className="flex gap-1">
                        {pipeline.outputPlatforms.map((p) => (
                          <PlatformBadge key={p} platform={p} />
                        ))}
                      </div>
                    </div>

                    <span className="text-xs text-white/40">
                      {pipeline.contentCount}{' '}
                      {pipeline.contentCount === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>

                <div className="ml-4 flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(pipeline)}
                    role="switch"
                    aria-checked={pipeline.isActive}
                    className={[
                      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                      pipeline.isActive ? 'bg-pix-pink' : 'bg-gray-200',
                    ].join(' ')}
                    title={pipeline.isActive ? 'Deactivate' : 'Activate'}
                  >
                    <span
                      className={[
                        'inline-block h-3.5 w-3.5 transform rounded-full bg-pix-deep shadow transition-transform',
                        pipeline.isActive ? 'translate-x-[18px]' : 'translate-x-0.5',
                      ].join(' ')}
                    />
                  </button>

                  <button
                    onClick={() => handleRun(pipeline.id)}
                    disabled={triggeringId === pipeline.id}
                    title="Trigger manual run"
                    className="rounded-md p-1.5 text-white/40 hover:bg-white/5 hover:text-white/60 disabled:opacity-50"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l14 9-14 9V3z" />
                    </svg>
                  </button>

                  <button
                    onClick={() => setEditingId(pipeline.id)}
                    title="Edit pipeline"
                    className="rounded-md p-1.5 text-white/40 hover:bg-white/5 hover:text-white/60"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>

                  <button
                    onClick={() => handleDelete(pipeline.id)}
                    disabled={deletingId === pipeline.id}
                    title="Delete pipeline"
                    className="rounded-md p-1.5 text-white/40 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </GlowCard>
          ))
        )}
      </div>

      {showCreate && (
        <PipelineModal
          title="New pipeline"
          initial={DEFAULT_FORM}
          saving={saving}
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editingId && editInitial && (
        <PipelineModal
          title="Edit pipeline"
          initial={editInitial}
          saving={saving}
          onSave={handleEdit}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
